import React, { useState, useRef, useEffect, lazy, Suspense } from "react";
import { DefaultPageLayout } from "../ui/layouts/DefaultPageLayout";
import { DropdownMenu } from "../ui/components/DropdownMenu";
import { FeatherFilePlus2 } from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import { IconButton } from "../ui/components/IconButton";
import { FeatherPaperclip } from "@subframe/core";
import { FeatherArrowUp } from "@subframe/core";
import { FeatherBookOpen } from "@subframe/core";
import { FeatherZap } from "@subframe/core";
import { FeatherTarget } from "@subframe/core";
import { FeatherEdit2 } from "@subframe/core";
import { FeatherSend } from "@subframe/core";
import { FeatherUser } from "@subframe/core";
import { FeatherBot } from "@subframe/core";
import { Button } from "../ui/components/Button";
import { AuthModal } from "../components/AuthModal";
import { useAuthContext } from "../contexts/AuthContext";
import { useStudyModules } from "../hooks/useStudyModules";
import { generateLearningMaterial } from "../services/aiGenerationService";
import { NavigationProps } from "../types";
import { Toast } from "../ui/components/Toast";
import { FeatherCheck } from "@subframe/core";
import { studyPageContentService } from "../services/studyPageContent";
import { Loader } from "../ui/components/Loader";

interface HomePageProps extends NavigationProps {}

export const HomePage: React.FC<HomePageProps> = ({ currentPage, onNavigate }) => {
  const [inputValue, setInputValue] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submittedText, setSubmittedText] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [uploadedFileType, setUploadedFileType] = useState("");
  const [uploadedFileContent, setUploadedFileContent] = useState("");
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuthContext();
  const { createStudyModule } = useStudyModules();

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputValue]);

  const handleSubmit = async () => {
    if (inputValue.trim()) {
      // Check if user is authenticated for advanced features
      if (!user && (uploadedFileName || inputValue.length > 100)) {
        setShowAuthModal(true);
        return;
      }
      
      setSubmittedText(inputValue.trim());
      setShowConfirmation(true);
      
      // Start generating immediately
      await handleGenerateLearningMaterial(inputValue.trim());
      
      setInputValue("");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        // Shift + Enter: Allow new line (default behavior)
        return;
      } else {
        // Enter: Submit
        event.preventDefault();
        handleSubmit();
      }
    }
  };

  const handleFileUpload = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const fileName = file.name.toLowerCase();
      const fileType = file.type;
      
      // Check for supported document types
      const isPDF = fileName.endsWith('.pdf') || fileType === 'application/pdf';
      const isDOCX = fileName.endsWith('.docx') || fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const isTXT = fileName.endsWith('.txt') || fileType === 'text/plain';

      if (!isPDF && !isDOCX && !isTXT) {
        alert('Please upload a supported document format: PDF, DOCX, or TXT');
        return;
      }

      setUploadedFileName(file.name);
      
      if (isPDF) {
        setUploadedFileType('PDF');
        setInputValue(`📄 PDF Document uploaded: ${file.name}\n\nI've uploaded a PDF document for analysis. Please create comprehensive learning materials based on this content.`);
      } else if (isDOCX) {
        setUploadedFileType('DOCX');
        setInputValue(`📄 Word Document uploaded: ${file.name}\n\nI've uploaded a Word document for analysis. Please create comprehensive learning materials based on this content.`);
      } else if (isTXT) {
        // For TXT files, we can read the content
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          if (content) {
            setUploadedFileContent(content);
            const preview = content.length > 500 ? content.substring(0, 500) + '...' : content;
            setInputValue(`📄 Text Document uploaded: ${file.name}\n\nContent preview:\n${preview}\n\nPlease create comprehensive learning materials based on this content.`);
          }
        };
        reader.readAsText(file);
      }
    }
    
    // Reset the input so the same file can be uploaded again
    event.target.value = '';
  };

  const handleEdit = () => {
    setInputValue(submittedText);
    setShowConfirmation(false);
    setIsGenerating(false);
  };

  const handleGenerateLearningMaterial = async (userInput?: string) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    const inputToUse = userInput || submittedText;
    setIsGenerating(true);

    try {
      // Use the user input directly as the title
      const moduleTitle = inputToUse.length > 50 
        ? inputToUse.substring(0, 50) + "..." 
        : inputToUse;

      // Create the study module first with the user input as title
      const moduleData = {
        title: moduleTitle,
        description: `Study module created from: "${inputToUse}"`,
        progress: 0,
        status: 'not-started' as const,
      };

      const newModule = await createStudyModule(moduleData);

      // Generate content using AI
      const generatedContent = await generateLearningMaterial(inputToUse, uploadedFileContent);

      // Save the generated content to the study page
      const fullContentToSave = {
        ...generatedContent,
        title: moduleTitle,
      };

      await studyPageContentService.saveStudyPageContent(newModule.id, fullContentToSave);

      // Store the module ID for navigation
      sessionStorage.setItem('studyModuleId', newModule.id);

      // Navigate to study page
      if (onNavigate) {
        onNavigate('study');
      }

      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 5000);

    } catch (error) {
      console.error('Error generating learning material:', error);
      
      // Create a basic module even if AI generation fails
      try {
        const moduleTitle = inputToUse.length > 50 
          ? inputToUse.substring(0, 50) + "..." 
          : inputToUse;

        const moduleData = {
          title: moduleTitle,
          description: `Study module created from: "${inputToUse}"`,
          progress: 0,
          status: 'not-started' as const,
        };

        const newModule = await createStudyModule(moduleData);

        // Create basic content structure
        const basicContent = {
          title: moduleTitle,
          summary: {
            simpleExplanation: `This study module covers the topic: ${inputToUse}. Content will be available soon.`,
            keyPoints: [
              {
                title: "Getting Started",
                description: "Begin exploring this topic",
                icon: "book-open"
              }
            ]
          },
          diagrams: {
            mindMap: "graph TD;\n  A[Main Topic] --> B[Subtopic 1];\n  A --> C[Subtopic 2];\n  A --> D[Subtopic 3];",
            linearFlow: "graph LR;\n  A[Start] --> B[Process];\n  B --> C[Result];",
            components: []
          },
          flashcards: [],
          quiz: {
            questions: []
          },
          audio: {
            transcript: "Audio content will be available soon",
            duration: 0
          }
        };

        await studyPageContentService.saveStudyPageContent(newModule.id, basicContent);
        sessionStorage.setItem('studyModuleId', newModule.id);

        if (onNavigate) {
          onNavigate('study');
        }

        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 5000);

      } catch (moduleError) {
        console.error('Error creating basic module:', moduleError);
        alert('An error occurred while creating your study module. Please try again.');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleBackToInput = () => {
    setShowConfirmation(false);
    setSubmittedText("");
    setUploadedFileName("");
    setUploadedFileType("");
    setUploadedFileContent("");
    setIsGenerating(false);
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'PDF': return '📄';
      case 'DOCX': return '📝';
      case 'TXT': return '📃';
      default: return '📄';
    }
  };

  if (showConfirmation) {
    return (
      <DefaultPageLayout 
        currentPage={currentPage} 
        onNavigate={onNavigate}
        onNewQuery={handleBackToInput}
        onShowAuth={() => setShowAuthModal(true)}
      >
        <div className="container max-w-none flex h-full w-full flex-col items-center justify-center bg-brand-50 py-12 px-4 md:px-8 lg:px-12">
          <div className="flex w-full max-w-[768px] grow shrink-0 basis-0 flex-col items-center justify-center gap-8 md:gap-12">
            {/* Chat Messages */}
            <div className="flex w-full flex-col gap-6">
              {/* User Message */}
              <div className="flex w-full justify-end">
                <div className="flex max-w-[80%] items-start gap-3">
                  <div className="flex flex-col items-end gap-2">
                    <div className="rounded-lg bg-brand-600 px-4 py-3 text-white shadow-sm">
                      <span className="text-body font-body">
                        {uploadedFileName && (
                          <div className="mb-2 text-brand-100 flex items-center gap-2">
                            <span className="text-lg">{getFileIcon(uploadedFileType)}</span>
                            <span className="font-medium">{uploadedFileName}</span>
                            <span className="text-xs bg-brand-500 px-2 py-1 rounded">{uploadedFileType}</span>
                          </div>
                        )}
                        {submittedText}
                      </span>
                    </div>
                    <span className="text-caption font-caption text-subtext-color">
                      Just now
                    </span>
                  </div>
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100">
                    <FeatherUser className="text-body font-body text-brand-700" />
                  </div>
                </div>
              </div>

              {/* AI Response */}
              <div className="flex w-full justify-start">
                <div className="flex max-w-[80%] items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100">
                    <FeatherBot className="text-body font-body text-brand-700" />
                  </div>
                  <div className="flex flex-col items-start gap-4">
                    <div className="rounded-lg bg-white border border-solid border-neutral-border px-4 py-3 shadow-sm">
                      <span className="text-body font-body text-default-font">
                        {uploadedFileName ? (
                          <>I'll help you create comprehensive learning materials from your uploaded {uploadedFileType} document "{uploadedFileName}". I can generate study guides, flashcards, quizzes, and audio lessons based on the content.</>
                        ) : (
                          <>I'll help you create comprehensive learning materials for "{submittedText}". I can generate study guides, flashcards, quizzes, and audio lessons tailored to your learning style.</>
                        )}
                      </span>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row items-start gap-3 w-full">
                      <Button
                        variant="neutral-secondary"
                        icon={<FeatherEdit2 />}
                        onClick={handleEdit}
                        className="w-full sm:w-auto"
                        disabled={isGenerating}
                      >
                        Edit Query
                      </Button>
                    </div>
                    
                    <span className="text-caption font-caption text-subtext-color">
                      MasteryMint • Just now
                    </span>
                  </div>
                </div>
              </div>

              {/* Generation Progress (shown when generating) */}
              {isGenerating && (
                <div className="flex w-full justify-start">
                  <div className="flex max-w-[80%] items-start gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100">
                      <FeatherBot className="text-body font-body text-brand-700" />
                    </div>
                    <div className="flex flex-col items-start gap-2">
                      <div className="rounded-lg bg-brand-50 border border-solid border-brand-200 px-4 py-3 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="flex space-x-1">
                            <div className="h-2 w-2 bg-brand-600 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                            <div className="h-2 w-2 bg-brand-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                            <div className="h-2 w-2 bg-brand-600 rounded-full animate-bounce"></div>
                          </div>
                          <span className="text-body font-body text-brand-800">
                            Creating your personalized learning materials...
                          </span>
                        </div>
                      </div>
                      <span className="text-caption font-caption text-subtext-color">
                        MasteryMint • Generating...
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {showSuccessToast && (
          <div className="fixed bottom-4 right-4 z-50">
            <Toast
              variant="success"
              icon={<FeatherCheck />}
              title="Learning Material Generated"
              description="Your study module has been created successfully."
            />
          </div>
        )}
        
        <AuthModal
          open={showAuthModal}
          onOpenChange={setShowAuthModal}
        />
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout 
      currentPage={currentPage} 
      onNavigate={onNavigate}
      onShowAuth={() => setShowAuthModal(true)}
    >
      <div className="container max-w-none flex h-full w-full flex-col items-center justify-center bg-brand-50 py-12 px-4 md:px-8 lg:px-12">

      
        
        <div className="flex w-full max-w-[768px] grow shrink-0 basis-0 flex-col items-center justify-center gap-8 md:gap-12">
          <div className="flex flex-col items-center justify-center gap-4 md:gap-6">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
              <img
                className="h-12 w-12 md:h-16 md:w-16 flex-none object-cover"
                src="/brain.png"
                alt="Brain Lightning Icon"
                loading="eager"
              />
              <span className="text-heading-2 font-heading-2 md:text-heading-1 md:font-heading-1 text-default-font">
                Learn Smarter with AI
              </span>
            </div>
            
          </div>
          
          <div className="flex w-full flex-col items-center justify-center gap-3 px-4">
            <div className="flex min-h-12 md:min-h-14 w-full max-w-[576px] flex-none items-end justify-center gap-2 rounded-md border border-solid border-brand-primary bg-default-background px-2 py-2 shadow-sm">
              <SubframeCore.DropdownMenu.Root>
                <SubframeCore.DropdownMenu.Trigger asChild={true}>
                  <IconButton
                    variant="brand-secondary"
                    size="medium"
                    className="md:h-10 md:w-10 mb-1"
                    icon={<FeatherPaperclip />}
                    onClick={handleFileUpload}
                  />
                </SubframeCore.DropdownMenu.Trigger>
                <SubframeCore.DropdownMenu.Portal>
                  <SubframeCore.DropdownMenu.Content
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    asChild={true}
                  >
                    <DropdownMenu>
                      <DropdownMenu.DropdownItem icon={<FeatherFilePlus2 />} onClick={handleFileUpload}>
                        Upload document (PDF, DOCX, TXT)
                      </DropdownMenu.DropdownItem>
                    </DropdownMenu>
                  </SubframeCore.DropdownMenu.Content>
                </SubframeCore.DropdownMenu.Portal>
              </SubframeCore.DropdownMenu.Root>
              
              <div className="flex grow shrink-0 basis-0 flex-col items-start self-stretch px-1">
                <textarea
                  ref={textareaRef}
                  className="w-full border-none bg-transparent text-body font-body text-default-font outline-none placeholder:text-neutral-400 resize-none min-h-[32px] max-h-[120px] overflow-y-auto py-1"
                  placeholder="Ask a concept or upload a document..."
                  value={inputValue}
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setInputValue(event.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                  rows={1}
                />
              </div>
              
              <IconButton
                variant={inputValue.trim() ? "brand-primary" : "brand-secondary"}
                size="medium"
                className={`md:h-10 md:w-10 mb-1 transition-all duration-200 ${
                  inputValue.trim() 
                    ? "opacity-100 scale-100 shadow-sm" 
                    : "opacity-60 scale-95"
                }`}
                icon={<FeatherArrowUp />}
                onClick={handleSubmit}
                disabled={!inputValue.trim()}
              />
            </div>
            
            
          </div>
          
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4 px-4">
            <div className="flex items-center gap-2 rounded-full border border-solid border-brand-200 bg-brand-50 px-3 py-2 md:px-4">
              <FeatherBookOpen className="text-caption md:text-body font-body text-brand-600" />
              <span className="text-caption font-caption text-brand-700 whitespace-nowrap">
                Instant Study Guides
              </span>
            </div>
            <div className="flex items-center gap-2 rounded-full border border-solid border-brand-200 bg-brand-50 px-3 py-2 md:px-4">
              <FeatherZap className="text-caption md:text-body font-body text-brand-600" />
              <span className="text-caption font-caption text-brand-700 whitespace-nowrap">
                AI-Powered Learning
              </span>
            </div>
            <div className="flex items-center gap-2 rounded-full border border-solid border-brand-200 bg-brand-50 px-3 py-2 md:px-4">
              <FeatherTarget className="text-caption md:text-body font-body text-brand-600" />
              <span className="text-caption font-caption text-brand-700 whitespace-nowrap">
                Personalized Content
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      
      <AuthModal
        open={showAuthModal}
        onOpenChange={setShowAuthModal}
      />
    </DefaultPageLayout>
  );
};
